import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import {
  PasswordFields,
  usePasswordFieldsState,
} from '../../../../components/inputs/PasswordFields';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { usersApi } from '../usersApi';
import type { UserCredentialIssued, UserDetail } from '../userTypes';

/**
 * The Credentials card's editor — set this account a new password.
 *
 * Same block as the create form: autogenerate and show it, or set one by hand. The account
 * must change it at next sign-in either way; an administrator has seen this value, so it is
 * a handover credential rather than the holder's own.
 *
 * Every session is revoked server-side, so the holder is signed out everywhere the moment
 * this saves.
 *
 * **No footer Autogenerate, and that is not an omission.** <PasswordFields> opens in its own
 * auto mode with a compliant password already minted, so every required field is filled before
 * the reader touches anything — the contract Autogenerate exists to meet is already met at
 * mount, in zero clicks. A footer button would also be the third control on this form named
 * for generating a password, beside the mode radio labelled Autogenerate and the Regenerate
 * button, and three names for one act is worse than none.
 */
export function ResetUserPasswordModal({
  user,
  onClose,
  onDone,
}: {
  user: UserDetail | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { state: passwordState, onChange: onPasswordChange } = usePasswordFieldsState();
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [issued, setIssued] = useState<UserCredentialIssued | null>(null);
  const [issuedPassword, setIssuedPassword] = useState('');

  if (!user) return null;

  const canSave = passwordState.valid && confirmed && !saving;

  async function save() {
    if (!user || !canSave) return;

    setSaving(true);
    setError(null);

    try {
      const result = await usersApi.resetPassword(user.id, {
        // Always supplied: the value is already on screen, so the server has nothing left to
        // mint and what lands cannot differ from what was shown.
        newPassword: passwordState.password,
        mustChangePassword: true,
      });

      setIssuedPassword(passwordState.password);
      setIssued(result);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not reset the password.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, {
        idPrefix: 'reset-user',
        // <PasswordFields> names its input for the control; the request field is the new one.
        fieldIds: { newPassword: 'reset-user-password' },
      });
    } finally {
      setSaving(false);
    }
  }

  // The new credential takes over the dialog rather than closing it: it has to be copied
  // before this closes, because nothing stores it.
  if (issued) {
    return (
      <Modal
        open
        onClose={() => {
          setIssued(null);
          onDone();
        }}
        title="Password reset"
        size="lg"
        id="user-reset-issued-modal"
        footer={
          <Button
            id="user-reset-issued-done"
            variant="primary"
            onClick={() => {
              setIssued(null);
              onDone();
            }}
          >
            Done
          </Button>
        }
      >
        <IssuedPassword
          email={issued.email}
          password={issued.generatedPassword ?? issuedPassword}
        />
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Reset password"
      size="xl"
      id="reset-user-password-modal"
      confirm={{
        id: 'reset-user-password-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="reset-user-password-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="reset-user-password-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The consequence goes on the form, where it can be read before the confirm is
          ticked — not appended to the tick itself.
        */}
        <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-200">
          {user.displayName} is signed out of every session and must change this password the
          next time they sign in.
        </p>

        <PasswordFields
          idPrefix="reset-user"
          disabled={saving}
          serverError={error?.fieldError('newPassword')}
          onChange={onPasswordChange}
          resetKey={user.id}
        />
      </div>
    </Modal>
  );
}

/** Shows the credential exactly once, with an explicit warning that it will not reappear. */
function IssuedPassword({ email, password }: { email: string; password: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!password) return;

    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the value is visible on screen regardless.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-slate-600">
        The password for <span className="font-mono tabular-nums">{email}</span> has been reset.
      </p>

      {password ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-800">
            This password is shown once and is not stored anywhere. Copy it now and deliver it
            through a channel you trust.
          </p>

          <div className="mt-2.5 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border border-amber-200 bg-white px-3 py-2 font-mono tabular-nums text-sm text-slate-800">
              {password}
            </code>
            <Button
              id="user-reset-issued-copy"
              variant="secondary"
              onClick={() => void copy()}
              leading={
                copied ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )
              }
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
